import { logger } from '@adornis/base/logging.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { generateAnswerModel, generateAnswerTemperature } from '../../../shared/models.js';
import { addCostToExam } from '../add-cost.js';
import { AssignmentProposalMessage, AssistantMessage, Conversation } from '../../conversation.js';
import type { Exam, ExamState } from '../../exam.js';
import { GRADI_STUDENT_SYSTEM_PROMPT, GRADI_SYSTEM_PROMPT } from '../../prompts.js';
import { buildExamContextString } from '../../utils/exam-context.js';
import { startStructureExtractionMutation } from '../start-structure-extraction.js';
// Imported directly from the (self-contained) tool module rather than the tools barrel: the barrel
// pulls grading-agent.tool → workflow-agent → agent-loop back in, which is the cycle shared-toolbox
// was created to avoid. memory.tool imports nothing that loops back here.
import { buildTeacherMemoryPromptSection } from '../tools/memory.tool.js';
import { getAvailableTools, getToolbox } from '../tools/shared-toolbox.js';

export type CanvasView =
  | 'boundingbox'
  | 'correction'
  | 'answers'
  | 'summary'
  | 'msedit'
  | 'structureeditor'
  | 'submissioncheck'
  | 'demoselector'
  | 'examcreator';

export const VALID_CANVAS_VIEWS: CanvasView[] = [
  'boundingbox',
  'correction',
  'answers',
  'summary',
  'msedit',
  'structureeditor',
  'submissioncheck',
  'demoselector',
  'examcreator',
];

const TOOL_LOG_TAG_PREFIX_BASE = '<tool-log';
const TOOL_LOG_TAG_SUFFIX = '</tool-log>';

/**
 * Synthetic tool gpt-5.4 emits when it tries to batch several tool calls in one turn. We do NOT support
 * parallel execution: when this appears (as the tool-log tag name or as a native tool_call) we reject the
 * turn with a specific reminder so the model retries with a single tool call.
 */
const PARALLEL_TOOL_WRAPPER = 'multi_tool_use.parallel';

/** Reminder pushed when the model tries to call several tools at once. */
const ONE_TOOL_PER_TURN_RUEGE =
  'Du hast versucht, mehrere Tools gleichzeitig (parallel) aufzurufen. Das ist NICHT erlaubt: Rufe pro Zug genau EIN Tool auf und warte dessen Ergebnis ab. Wenn du Daten zu mehreren Klausuren, Schülern oder Aufgaben brauchst, bündle sie in EINEM Aufruf über die Listen-Parameter (z.B. mehrere Einträge in "exams" oder mehrere "studentNames"), statt mehrere Aufrufe zu machen.';

/**
 * Extract tool name from opening tag: <tool-log tool="tool_name" step="...">
 */
const extractToolNameFromTag = (content: string): string | null => {
  const match = /tool="([^"]+)"/i.exec(content);
  return match?.[1] ?? null;
};

const normalizeToolLogTagMessage = (content: string): string | null => {
  const trimmed = content.trim();
  if (!trimmed.startsWith(TOOL_LOG_TAG_PREFIX_BASE)) return null;
  const suffixIndex = trimmed.indexOf(TOOL_LOG_TAG_SUFFIX);
  if (suffixIndex === -1) return `${trimmed}${TOOL_LOG_TAG_SUFFIX}`;
  return trimmed.slice(0, suffixIndex + TOOL_LOG_TAG_SUFFIX.length);
};

const buildHealedToolLogMessage = (toolName: string, step: string, rawBody: string): string => {
  const safeToolName = String(toolName || 'unknown_tool')
    .replace(/"/g, "'")
    .trim();
  const safeStep = String(step || 'Toolschritt ausfuehren')
    .replace(/"/g, "'")
    .trim();
  const body = String(rawBody || '').trim();
  return `<tool-log tool="${safeToolName}" step="${safeStep}">${body}</tool-log>`;
};

const NO_EXAM_CONTEXT = 'Aktuell ist noch keine Klausur mit diesem Chat verknüpft. Bitte lade zuerst die Klausur hoch.';

/**
 * Map a stored conversation message doc to a chat-completions message. AssignmentProposalMessage is
 * rendered via its agent-content builder; SystemInfoMessage counts as a user turn.
 */
const mapMessageToPayload = (m: any): { role: 'user' | 'assistant'; content: string } | null => {
  if (!m) return null;
  const role =
    m._class === 'UserMessage' || m._class === 'user' || m._class === 'SystemInfoMessage' ? 'user' : 'assistant';
  if (m._class === 'AssignmentProposalMessage') {
    try {
      return { role, content: AssignmentProposalMessage.buildAgentContentForPlainObject(m) };
    } catch {
      /* fall through to default */
    }
  }
  return { role, content: m.content || '' };
};

export interface AgentStreamingLoopParams {
  conversationId: string;
  examId?: string | null;
  /** Exam doc used to build the exam-context section of the system prompt (null = no exam linked). */
  exam?: Exam | Record<string, unknown> | null;
  /** Exam state used to pick available tools. Defaults to exam.examState. */
  examState?: ExamState;
  /** Conversation language for the system prompt. */
  language: string;
  /** Current user's role — picks the persona/knowledge section of the system prompt. Omitted (e.g.
   * the server-initiated workflow agent, which only ever runs in a teacher/grading context) defaults
   * to 'teacher', matching every account that existed before the student role was introduced. */
  role?: 'teacher' | 'student';
  /** Conversation message docs as history — the caller prepares this (appended question / existing list). */
  messages: any[];
  /** Extra text appended after the exam context in the system prompt (e.g. UI interaction context). */
  systemContextSuffix?: string;
  /** Parsed UI canvas context (see generate-answer.ts's CanvasContext) — threaded into tool handlers
   * (via toolExamContext.canvasContext) as a fallback default for tools that can act on whatever the
   * teacher currently has visible in the canvas, e.g. set_task_score. Opaque here; tool handlers cast
   * it to the shape they expect. */
  canvasContext?: unknown;
  /** Extra messages appended after history (e.g. a workflow notification, or assistant answer + merge guide). */
  trailingMessages?: Array<{ role: string; content: string }>;
  /** Base Conversation used when spreading streamed previews back to the sink. */
  existingConversation: Conversation;
  /**
   * Seed for the streamed/returned content. Used to CONTINUE an existing answer: the loop prepends
   * this to every preview and to the final content, so a follow-up pass extends the same message
   * instead of starting a new one (e.g. merging a mid-answer workflow notification).
   */
  contentPrefix?: string;
  /** Streaming sink — subscriber.next for the client turn, the conversation change stream for workflow runs. */
  emit: (conversation: Conversation) => void;
}

export interface AgentStreamingLoopResult {
  finalAssistantContent: string;
  latestMessages: any[];
  canvasToOpen: CanvasView | null;
  pendingDownload: string | null;
  startStructureExtraction: boolean;
  suppressAssistantFollowUp: boolean;
}

/**
 * The shared streaming agentic loop. Streams tool-log announcements and the final answer
 * token-by-token through `emit`, executes tool calls (one per turn), and heals malformed
 * tool-log handshakes. Both the user-facing generateAnswer subscription and the
 * server-initiated workflow agent drive this — only the emit sink and finalization differ.
 *
 * Does NOT persist the final message or complete the stream; the caller owns finalization
 * (DB write, pendingCanvasOpen, task cleanup, subscriber.complete).
 */
export async function runAgentStreamingLoop(params: AgentStreamingLoopParams): Promise<AgentStreamingLoopResult> {
  const { conversationId, examId, existingConversation, emit } = params;

  const router = getLLMRouter({}, 'interactive');
  const toolbox = getToolbox();
  const examState = params.examState ?? (((params.exam as any)?.examState ?? {}) as ExamState);
  const toolsForCurrentExamState = getAvailableTools(examState, params.exam as any, params.role);

  // Build the system prompt + chat payload here so callers don't re-declare it.
  const examContextString = params.exam ? await buildExamContextString(params.exam as any, false) : NO_EXAM_CONTEXT;

  // Personalization: load this teacher's stored memory (preferences/rules) and inject it between the
  // persona prompt and the exam context. userID comes from the exam, falling back to the conversation
  // (so it also works in pure chat with no exam linked). Rules are filtered to the current exam's
  // subject (general + subject-matching) so no subject tagging needs to reach the agent.
  const memoryUserID = (params.exam as any)?.userID ?? (params.existingConversation as any)?.userID ?? null;
  const memorySubject = (params.exam as any)?.subject ?? null;
  const teacherMemorySection = await buildTeacherMemoryPromptSection(memoryUserID, memorySubject);

  const personaPrompt =
    params.role === 'student' ? GRADI_STUDENT_SYSTEM_PROMPT(params.language) : GRADI_SYSTEM_PROMPT(params.language);
  const systemContent = `${personaPrompt || ''}\n\n${
    teacherMemorySection ? `${teacherMemorySection}\n\n` : ''
  }${examContextString}${params.systemContextSuffix ? `\n${params.systemContextSuffix}` : ''}`;
  const messagesPayload: any[] = [
    { role: 'system', content: systemContent },
    ...params.messages.map(mapMessageToPayload).filter(Boolean),
    ...(params.trailingMessages ?? []),
  ];

  let finalAssistantContent = '';
  // Seeded with contentPrefix so a continuation pass appends to (rather than replaces) the answer so far.
  let streamedAssistantContent = params.contentPrefix ?? '';
  let latestMessages = params.messages;
  let canvasToOpen: CanvasView | null = null;
  let pendingDownload: string | null = null;
  let startStructureExtraction = false;
  let suppressAssistantFollowUp = false;
  let agentLoopCount = 0;
  const MAX_AGENT_LOOPS = 10;
  let isToolCallOnlyPath = false;

  // Agentic loop: continue while tool calls exist
  agentLoop: while (agentLoopCount < MAX_AGENT_LOOPS) {
    agentLoopCount++;
    logger.info({ agentLoopCount, MAX_AGENT_LOOPS, context: 'GA' }, 'Agent Loop iteration');

    // Get response from LLM
    const response = await router.chat.completions.create({
      stream: true,
      model: generateAnswerModel,
      provider: 'openrouter',
      temperature: generateAnswerTemperature,
      tools: toolsForCurrentExamState,
      messages: messagesPayload,
      // Not part of the chat-completions type, forwarded verbatim by the router — same mechanism
      // streamExamSource uses in exam-creation-agent.ts to get a usage-bearing final chunk for cost tracking.
      ...({ stream_options: { include_usage: true } } as any),
    } as any);

    const currentMessage = {
      content: '',
      toolCalls: [] as Array<{ id: string; name: string; arguments: string }>,
    };
    const toolCallsMap: Record<number, { id: string; name: string; arguments: string }> = {};
    let sawToolCallInStream = false;
    let toolPrefixValidated = false;
    let isBufferingTag = true; // Micro-buffer: blocks UI rendering until first tag line is validated
    let validatedToolName: string | null = null;
    let usageCarrier: any = null;

    // Stream and collect response
    for await (const chunk of response as any) {
      if ((chunk as any)?.usage) usageCarrier = chunk;

      const deltaToolCalls = chunk.choices[0]?.delta?.tool_calls;
      if (deltaToolCalls && deltaToolCalls.length > 0) {
        sawToolCallInStream = true;
        for (const tc of deltaToolCalls) {
          if (typeof tc.index !== 'number') continue;
          if (!toolCallsMap[tc.index]) {
            toolCallsMap[tc.index] = { id: tc.id || '', name: tc.function?.name || '', arguments: '' };
          }
          const toolCallEntry = toolCallsMap[tc.index];
          if (!toolCallEntry) continue;

          if (tc.id) toolCallEntry.id = tc.id;
          if (tc.function?.name) toolCallEntry.name = tc.function.name;
          if (tc.function?.arguments) {
            toolCallEntry.arguments += tc.function.arguments;
          }
        }
      }

      const contentChunk = chunk.choices[0]?.delta.content ?? '';
      if (contentChunk) {
        currentMessage.content += contentChunk;

        // ===== 1. MICRO-BUFFER VALIDATION =====
        // Validate tag schema dynamically before pushing tokens to user screen.
        // If the response does not start with <tool-log, release buffering immediately.
        if (isBufferingTag) {
          const trimmedStart = currentMessage.content.trimStart();
          const isPotentialPrefix =
            TOOL_LOG_TAG_PREFIX_BASE.startsWith(trimmedStart) || trimmedStart.startsWith(TOOL_LOG_TAG_PREFIX_BASE);

          if (!isPotentialPrefix) {
            isBufferingTag = false;
          } else {
            if (!trimmedStart.includes('>')) {
              continue;
            }

            const closingBracketIndex = trimmedStart.indexOf('>');
            const fullOpeningTag = trimmedStart.slice(0, closingBracketIndex + 1);

            validatedToolName = extractToolNameFromTag(fullOpeningTag);
            const validToolNames = toolsForCurrentExamState.map(t => t.function?.name);

            // Hard gate: the model tried to batch tools in one turn via `multi_tool_use.parallel`. We don't
            // support parallel execution — reject the turn with a specific reminder to use a single tool call.
            if (validatedToolName === PARALLEL_TOOL_WRAPPER) {
              logger.info({ context: 'GA' }, 'Parallel-Tool-Call blocked; only one tool per turn allowed');
              messagesPayload.push({ role: 'system', content: ONE_TOOL_PER_TURN_RUEGE });
              continue agentLoop;
            }

            if (!validatedToolName || !validToolNames.includes(validatedToolName)) {
              logger.info(
                { validatedToolName, context: 'GA' },
                'Stream aborted due to illegal tool name in tag; retrying immediately',
              );

              // Strikte Rüge direkt in den Verlauf pushen
              messagesPayload.push({
                role: 'system',
                content:
                  'Deine letzte Tool-Ankündigung war ungültig. Wenn du ein Tool aufrufst, antworte zuerst mit GENAU EINER Nachricht im Format <tool-log tool="tool_name" step="kurze Aktion (3-6 Worte)">...</tool-log> und rufe dann das Tool auf.',
              });

              // Sofortiger Abbruch und lautloser Neustart des gesamten Turns
              continue agentLoop;
            }

            // Handshake verified -> release buffer and start real-time streaming
            isBufferingTag = false;
            toolPrefixValidated = true;
          }
        }

        // ===== 2. INSTANT SELF-TALK PROTECTION =====
        // Intercept conversational drift or pseudo-answers within the streaming phase
        if (currentMessage.content.includes(TOOL_LOG_TAG_SUFFIX)) {
          const parts = currentMessage.content.split(TOOL_LOG_TAG_SUFFIX);
          const trailingConversationalJunk = parts[1] || '';

          if (trailingConversationalJunk.trim().length > 0) {
            logger.info({ context: 'GA' }, 'Stream aborted: model printed text after tool-log closing tag; truncating');
            currentMessage.content = `${parts[0]}${TOOL_LOG_TAG_SUFFIX}`;
            break; // Abort instantly to keep the UI presentation clean
          }
        }

        // ===== 3. SAFE LIVE STREAM UPDATE =====
        if (sawToolCallInStream && !toolPrefixValidated) {
          continue;
        }

        const normalizedPreviewTag = normalizeToolLogTagMessage(currentMessage.content) ?? currentMessage.content;
        const previewContent = `${streamedAssistantContent}${normalizedPreviewTag}`;
        emit(
          new Conversation({
            ...existingConversation,
            messages: [...latestMessages, new AssistantMessage({ content: previewContent }).toObject()],
            thinking: true,
            // Not resurrected from the turn-start snapshot — a tool run earlier in THIS turn (e.g.
            // confirming a structure) can trigger the client to clear these mid-turn, and this is a
            // transient preview, not the authoritative value; persistAndEmitFinalTurn re-reads it fresh.
            pendingCanvasOpen: undefined,
            pendingDownload: undefined,
          }),
        );
      }
    }

    await addCostToExam(usageCarrier ?? {}, examId ?? undefined, 'generateAnswer');

    currentMessage.toolCalls = Object.values(toolCallsMap);
    const responseContent = normalizeToolLogTagMessage(currentMessage.content) ?? (currentMessage.content || '');
    logger.info(
      { turn: agentLoopCount, responseContent: JSON.stringify(responseContent), context: 'GA' },
      'Turn response content',
    );

    const normalizedToolLogMessage = normalizeToolLogTagMessage(currentMessage.content);

    // ===== 4. FORCED TOOL-CHOICE ARGUMENT EXTRACTION =====
    // If valid tag exists but native toolCalls array is empty, force structured parameter injection
    if (normalizedToolLogMessage && currentMessage.toolCalls.length === 0 && validatedToolName) {
      logger.info({ validatedToolName, context: 'GA' }, 'Fallback: extracting tool arguments via forced tool_choice');
      try {
        // Add the cleaned assistant tool-log to context so fallback arg generation
        // can ground itself on the just-produced reasoning.
        const temporaryPayload = [
          ...messagesPayload,
          {
            role: 'assistant',
            content: normalizedToolLogMessage,
          },
        ];

        const fallbackResponse = await router.chat.completions.create({
          stream: false,
          model: generateAnswerModel,
          provider: 'openrouter',
          temperature: 0, // Hard deterministic pass
          tools: toolsForCurrentExamState,
          tool_choice: {
            type: 'function',
            function: { name: validatedToolName },
          },
          messages: [
            ...temporaryPayload,
            {
              role: 'system',
              content: `Provide ONLY the JSON arguments for the function "${validatedToolName}" based on your tool-log thoughts.`,
            },
          ],
        });

        await addCostToExam(fallbackResponse, examId ?? undefined, 'generateAnswerToolArgs');

        const forcedPayload = fallbackResponse.choices[0]?.message?.tool_calls?.[0] as any;
        if (forcedPayload) {
          currentMessage.toolCalls = [
            {
              id: forcedPayload.id || `forced-${conversationId}-${agentLoopCount}`,
              name: forcedPayload.function?.name || validatedToolName,
              arguments: forcedPayload.function?.arguments || '{}',
            },
          ];
          logger.info(
            { toolCallArguments: currentMessage.toolCalls[0]?.arguments, context: 'GA' },
            'Fallback resolved: successfully fetched tool parameters',
          );
        }
      } catch (fallbackError) {
        logger.error({ err: fallbackError, context: 'GA' }, 'Fallback failure: argument extraction pass crashed');
      }
    }

    if (normalizedToolLogMessage && currentMessage.toolCalls.length === 0) {
      logger.info({ context: 'GA' }, 'Tool-log tag detected without actual tool_calls; freezing UI state and retrying');

      // 1. UI-TEXT SICHERN: Bereits gestreamten Text einfrieren, damit er nicht verschwindet
      const trailingLineBreak = normalizedToolLogMessage.endsWith('\n') ? '' : '\n';
      streamedAssistantContent += `${normalizedToolLogMessage}${trailingLineBreak}`;

      // 4. KI-KONTEXT: Das generierte Log als Assistant-Kontext + die Rüge übergeben
      messagesPayload.push({
        role: 'assistant',
        content: normalizedToolLogMessage,
      });
      messagesPayload.push({
        role: 'system',
        content:
          'Du hast das Tool im <tool-log> angekündigt, aber keinen echten Tool-Aufruf gesendet. Führe JETZT ausschließlich den nativen Function-Call über tool_calls aus. Generiere keinen erneuten Text oder XML-Tags.',
      });

      isToolCallOnlyPath = true;
      continue; // Turn fehlerfrei und ohne Text-Wipe neu starten
    }

    if (currentMessage.toolCalls.length > 0) {
      let normalizedToolLogMessageForToolCall = normalizeToolLogTagMessage(currentMessage.content);
      const firstToolCallName = currentMessage.toolCalls[0]?.name || validatedToolName || 'unknown_tool';

      if (isToolCallOnlyPath) {
        isToolCallOnlyPath = false;
        logger.info({ context: 'GA' }, 'Tool-only path: skipping XML tag validation and healers for this turn');
      } else {
        // [NEU] Healing bei Tool-Namens-Mismatch (Tag != nativer Aufruf)
        // Falls das Modell Tool A im Tag nennt, aber nativ Tool B aufruft,
        // korrigieren wir den Tag serverseitig statt den Turn abzubrechen.
        if (normalizedToolLogMessage && validatedToolName && validatedToolName !== firstToolCallName) {
          logger.info(
            { validatedToolName, firstToolCallName, context: 'GA' },
            'Tool mismatch healed: tag named different tool than actual call; adjusting tag attributes',
          );

          const fallbackStep = toolbox.getDescByToolName(firstToolCallName, params.language);
          const innerThoughts = currentMessage.content.replace(/<tool-log[^>]*>/i, '').replace('</tool-log>', '');

          const healedMessage = buildHealedToolLogMessage(firstToolCallName, fallbackStep, innerThoughts);
          currentMessage.content = healedMessage;
          normalizedToolLogMessageForToolCall = normalizeToolLogTagMessage(healedMessage);
        }

        // Server-side healer (Fall C): tool_call exists, but response missed a valid <tool-log ...> wrapper.
        // Preserve streamed text and convert it into a valid tool-log block without re-calling the model.
        if (
          !normalizedToolLogMessageForToolCall &&
          currentMessage.content &&
          currentMessage.content.trim().length > 0
        ) {
          const fallbackStep = toolbox.getDescByToolName(firstToolCallName, params.language);
          const healedToolLogMessage = buildHealedToolLogMessage(
            firstToolCallName,
            fallbackStep,
            currentMessage.content,
          );

          currentMessage.content = healedToolLogMessage;
          normalizedToolLogMessageForToolCall = normalizeToolLogTagMessage(healedToolLogMessage);

          logger.info(
            { turn: agentLoopCount, tool: firstToolCallName, step: JSON.stringify(fallbackStep), context: 'GA' },
            'Tool-log healed',
          );
        }

        if (!normalizedToolLogMessageForToolCall) {
          logger.info({ context: 'GA' }, 'Tool-call response missing valid tool-log tag; retrying agent response');
          messagesPayload.push({
            role: 'system',
            content:
              'Deine letzte Tool-Ankündigung war ungültig. Wenn du ein Tool aufrufst, antworte zuerst mit GENAU EINER Nachricht im Format <tool-log tool="tool_name" step="kurze Aktion (3-6 Worte)">...</tool-log> und rufe dann das Tool auf.',
          });
          continue;
        }
      }

      if (!isToolCallOnlyPath) {
        const normalizedToolLogMessageForToolCallSafe = normalizedToolLogMessageForToolCall!;
        const trailingLineBreak = normalizedToolLogMessageForToolCallSafe.endsWith('\n') ? '' : '\n';
        const persistedToolLogAnnouncement = `${normalizedToolLogMessageForToolCallSafe}${trailingLineBreak}`;
        streamedAssistantContent += persistedToolLogAnnouncement;

        emit(
          new Conversation({
            ...existingConversation,
            messages: [...latestMessages, new AssistantMessage({ content: streamedAssistantContent }).toObject()],
            thinking: true,
            // See the same fields in the streaming-preview emit above.
            pendingCanvasOpen: undefined,
            pendingDownload: undefined,
          }),
        );
      }
    }

    // If no tool calls, this is the final response - break loop
    if (!currentMessage.toolCalls || currentMessage.toolCalls.length === 0) {
      finalAssistantContent = `${streamedAssistantContent}${currentMessage.content}`;
      break;
    }

    // Execute exactly one tool per turn (sequential execution). We do not run tools in parallel; if the model
    // still emits several native tool_calls, we answer only the first and advertise only the first, so the
    // assistant message stays consistent with the single tool result we push below (otherwise the provider
    // rejects the next request with "No tool output found for function call …").
    const toolCall = currentMessage.toolCalls[0];
    if (!toolCall) {
      logger.info({ context: 'GA' }, 'No tool call found but toolCalls array is not empty');
      continue;
    }

    if (currentMessage.toolCalls.length > 1) {
      logger.info(
        { toolCallCount: currentMessage.toolCalls.length, toolName: toolCall.name, context: 'GA' },
        'Multiple tool-calls received; executing only the first and discarding the rest',
      );
    }

    // Add assistant message with the single executed tool call to payload
    messagesPayload.push({
      role: 'assistant',
      content: normalizeToolLogTagMessage(currentMessage.content) ?? (currentMessage.content || ''),
      tool_calls: [
        {
          id: toolCall.id,
          type: 'function',
          function: { name: toolCall.name, arguments: toolCall.arguments },
        },
      ],
    });

    // Capture original message count before tool handling
    const originalMessageCountBeforeToolHandling = latestMessages.length;

    let toolResultContent = '';

    if (toolCall.name === 'send_assignment_confirmation_message') {
      suppressAssistantFollowUp = true;
    }

    const toolExamContext = {
      _id: examId,
      conversationId,
      examState,
      canvasContext: params.canvasContext ?? null,
    } as unknown as Exam;

    try {
      const toolCallArgs = JSON.parse(toolCall.arguments || '{}');
      logger.info(
        { toolName: toolCall.name, toolCallArgs: JSON.stringify(toolCallArgs), context: 'GA' },
        'Executing tool with args',
      );
      const toolResult = (await toolbox.executeTool(toolCall.name, toolExamContext, toolCallArgs)) as Record<
        string,
        unknown
      >;

      const toolContent = toolResult?.content;
      if (typeof toolContent === 'string' && toolContent.trim().length > 0) {
        toolResultContent = toolContent;
      } else {
        try {
          toolResultContent = JSON.stringify(toolResult);
        } catch (e) {
          toolResultContent = String(toolResult ?? '');
        }
      }

      if (
        typeof toolResult?.canvasToOpen === 'string' &&
        VALID_CANVAS_VIEWS.includes(toolResult.canvasToOpen as CanvasView)
      ) {
        canvasToOpen = toolResult.canvasToOpen as CanvasView;
      }

      if (typeof toolResult?.pendingDownload === 'string' && toolResult.pendingDownload.length > 0) {
        pendingDownload = toolResult.pendingDownload;
      }

      if (toolResult?.startStructureExtraction === true) {
        startStructureExtraction = true;
      }

      if (toolResult?.suppressAssistantFollowUp === true) {
        suppressAssistantFollowUp = true;
      }
    } catch (toolError) {
      logger.error({ err: toolError, toolName: toolCall.name, context: 'GA' }, 'Error executing tool');
      toolResultContent = `Fehler beim Ausführen von ${toolCall.name}.`;
    }

    logger.info({ toolName: toolCall.name, toolResultContent, context: 'GA' }, 'Tool result received');
    messagesPayload.push({ role: 'tool', tool_call_id: toolCall.id, content: toolResultContent });

    const shouldBreakAfterRefresh = toolCall.name === 'send_assignment_confirmation_message';

    // Refresh conversation from DB and append only new messages created
    // during tool handling; keep current streamed state intact.
    try {
      const refreshedConversation = await (await getCollection(Conversation)).findOne({ _id: conversationId });
      if (refreshedConversation && Array.isArray((refreshedConversation as any).messages)) {
        const refreshedMessages = (refreshedConversation as any).messages as any[];
        const appendedMessages = refreshedMessages.slice(originalMessageCountBeforeToolHandling);
        // add back previously streamed content to new assignment proposal created by tool calls to avoid content jumps in the UI
        if (shouldBreakAfterRefresh && streamedAssistantContent.trim().length > 0) {
          const latestAssignmentProposal = appendedMessages
            .slice()
            .reverse()
            .find(msg => msg?._class === 'AssignmentProposalMessage');

          if (latestAssignmentProposal) {
            const streamedPrefix = streamedAssistantContent.trimEnd();
            const currentContent =
              typeof latestAssignmentProposal.content === 'string' ? latestAssignmentProposal.content.trimStart() : '';
            const contentWithPrefix = currentContent ? `${streamedPrefix}\n\n${currentContent}` : streamedPrefix;

            if (latestAssignmentProposal.content !== contentWithPrefix) {
              latestAssignmentProposal.content = contentWithPrefix;
              if (typeof latestAssignmentProposal.baseContent === 'string') {
                latestAssignmentProposal.baseContent = contentWithPrefix;
              }
            }
          }
        }

        latestMessages = [...latestMessages, ...appendedMessages];
      }
    } catch (err) {
      logger.error({ err, context: 'GA' }, 'Failed to refresh conversation after tool calls');
    }

    // Break loop after DB refresh so tool-created messages are not overwritten.
    if (shouldBreakAfterRefresh) {
      logger.info({ context: 'GA' }, 'Loop interrupted: send_assignment_confirmation_message executed');
      break;
    }

    // Continue loop to get next response with tool results included
  }

  if (agentLoopCount >= MAX_AGENT_LOOPS) {
    logger.info({ agentLoopCount, MAX_AGENT_LOOPS, context: 'GA' }, 'Agent loop reached max iterations');
  }

  return {
    finalAssistantContent,
    latestMessages,
    canvasToOpen,
    pendingDownload,
    startStructureExtraction,
    suppressAssistantFollowUp,
  };
}

/**
 * Shared turn finalization used by both the user-facing generateAnswer and the workflow agent:
 * kicks the extraction pipeline if a tool requested it, builds the final message list (respecting
 * suppressAssistantFollowUp), then persists + emits the final state. Keeps the two callers from
 * re-declaring identical finalization logic.
 */
export async function finalizeAgentTurn(params: {
  conversationId: string;
  existingConversation: Conversation;
  examId?: string | null;
  language?: string;
  loopResult: AgentStreamingLoopResult;
  /** false ends a user turn; workflow passes also end false once their tasks are drained. */
  thinking?: boolean;
  emit: (conversation: Conversation) => void;
}): Promise<void> {
  const { conversationId, existingConversation, examId, language, loopResult, thinking, emit } = params;
  const {
    finalAssistantContent,
    latestMessages,
    canvasToOpen,
    pendingDownload,
    startStructureExtraction,
    suppressAssistantFollowUp,
  } = loopResult;

  if (startStructureExtraction && examId) {
    void startStructureExtractionMutation(String(examId), conversationId)().catch(err =>
      logger.error({ err, context: 'AgentLoop' }, 'Failed to kick off extraction pipeline'),
    );
  }

  const finalMessages = suppressAssistantFollowUp
    ? [...latestMessages]
    : [...latestMessages, new AssistantMessage({ content: finalAssistantContent }).toObject()];

  await persistAndEmitFinalTurn({
    conversationId,
    existingConversation,
    finalMessages,
    canvasToOpen,
    pendingDownload,
    language,
    thinking,
    emit,
  });
}

/**
 * Persist the final conversation state (one DB write) and emit it.
 * `pendingCanvasOpen` is delivered to the client via streamConversationByIdSubscription
 * (the client watches that field), so canvas-opening tools take effect on every path.
 */
export async function persistAndEmitFinalTurn(params: {
  conversationId: string;
  existingConversation: Conversation;
  finalMessages: any[];
  canvasToOpen: CanvasView | null;
  pendingDownload?: string | null;
  language?: string;
  /**
   * thinking state to persist/emit. The workflow agent keeps this `true` between iterations
   * so it holds its thinking-lock across multiple pending tasks; the user turn ends with `false`.
   */
  thinking?: boolean;
  emit: (conversation: Conversation) => void;
}): Promise<void> {
  const { conversationId, existingConversation, finalMessages, canvasToOpen, pendingDownload, language, emit } = params;
  const thinking = params.thinking ?? false;

  await (
    await getRawCollection(Conversation._collectionName)
  ).updateOne({ _id: conversationId } as any, {
    $set: {
      messages: finalMessages,
      thinking,
      ...(canvasToOpen ? { pendingCanvasOpen: canvasToOpen } : {}),
      ...(pendingDownload ? { pendingDownload } : {}),
      lastUpdated: new Date(),
    },
  });

  // Re-read rather than trust `existingConversation` (a turn-start snapshot): a tool run earlier in
  // THIS turn (e.g. confirming a structure) can trigger the client to clear these fields mid-turn via
  // clearPendingCanvasOpenMutation, and spreading the stale snapshot would resurrect an already-handled
  // canvas-open/download request right after the client correctly cleared it.
  const currentConversation = await (
    await getCollection(Conversation)
  ).findOne({ _id: conversationId }, { projection: { pendingCanvasOpen: 1, pendingDownload: 1 } });
  const currentPendingCanvasOpen = canvasToOpen ?? (currentConversation as any)?.pendingCanvasOpen ?? undefined;
  const currentPendingDownload = pendingDownload ?? (currentConversation as any)?.pendingDownload ?? undefined;

  emit(
    new Conversation({
      ...existingConversation,
      messages: finalMessages,
      thinking,
      ...(language ? { language } : {}),
      pendingCanvasOpen: currentPendingCanvasOpen,
      pendingDownload: currentPendingDownload,
      lastUpdated: new Date(),
    }),
  );
}
